import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/** The New run drawer's width before anyone drags it (the mockup's). */
export const BULK_SHELL_DRAWER_WIDTH = 560;

interface UIState {
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;
  theme: 'light' | 'dark' | 'system';
  // Hidden-by-default sidebar entries (e.g. Tenants) are only rendered
  // when their key is present in this set. Toggled from the devtools
  // console via window.showNav('tenants') / hideNav('tenants').
  hiddenNavEnabled: Record<string, boolean>;
  /** Bulk Shell's New run drawer width in px; the user drags its left edge. */
  bulkShellDrawerWidth: number;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebarCollapse: () => void;
  setTheme: (theme: 'light' | 'dark' | 'system') => void;
  setHiddenNavEnabled: (key: string, enabled: boolean) => void;
  setBulkShellDrawerWidth: (width: number) => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      sidebarOpen: true,
      sidebarCollapsed: false,
      theme: 'light',
      hiddenNavEnabled: {},
      bulkShellDrawerWidth: BULK_SHELL_DRAWER_WIDTH,

      toggleSidebar: () =>
        set((state) => ({ sidebarOpen: !state.sidebarOpen })),

      setSidebarOpen: (open) => set({ sidebarOpen: open }),

      toggleSidebarCollapse: () =>
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

      setBulkShellDrawerWidth: (bulkShellDrawerWidth) => set({ bulkShellDrawerWidth }),

      setHiddenNavEnabled: (key, enabled) =>
        set((state) => {
          const next = { ...state.hiddenNavEnabled };
          if (enabled) next[key] = true;
          else delete next[key];
          return { hiddenNavEnabled: next };
        }),

      setTheme: (theme) => {
        const root = window.document.documentElement;
        root.classList.remove('light', 'dark');

        let appliedTheme: 'light' | 'dark';
        if (theme === 'system') {
          appliedTheme = window.matchMedia('(prefers-color-scheme: dark)')
            .matches
            ? 'dark'
            : 'light';
        } else {
          appliedTheme = theme;
        }
        root.classList.add(appliedTheme);

        set({ theme });
      },
    }),
    {
      name: 'ui-storage',
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        theme: state.theme,
        hiddenNavEnabled: state.hiddenNavEnabled,
        bulkShellDrawerWidth: state.bulkShellDrawerWidth,
      }),
    }
  )
);
